<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import ArrowsAiAssistantsAPI from 'dashboard/api/arrowsAi/assistants';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import Button from 'dashboard/components-next/button/Button.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter';

const formatReply = text => new MessageFormatter(text || '').formattedMessage;

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant } = useArrowsAiAssistants();
const assistant = computed(() => {
  const current = getAssistant(route.params.assistantId);
  if (current) rememberAssistant(current.id);
  return current;
});
const message = ref('');
const replies = ref([]);
const isSending = ref(false);

const sendPreview = async () => {
  const trimmed = message.value.trim();
  if (!trimmed || isSending.value) return;

  replies.value = [
    ...replies.value,
    { id: Date.now(), role: 'user', text: trimmed },
  ];
  message.value = '';
  isSending.value = true;

  try {
    const { data } = await ArrowsAiAssistantsAPI.sendPlaygroundMessage(
      assistant.value.id,
      replies.value.map(reply => ({ role: reply.role, content: reply.text }))
    );
    replies.value = [
      ...replies.value,
      { id: Date.now() + 1, role: 'assistant', text: data.reply },
    ];
  } catch (error) {
    const errorMessage = parseAPIErrorResponse(error);
    useAlert(
      typeof errorMessage === 'string'
        ? errorMessage
        : t('ARROWS_AI.PLAYGROUND.ERROR')
    );
  } finally {
    isSending.value = false;
  }
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendPreview();
};
</script>

<template>
  <PageLayout :header-title="t('ARROWS_AI.PLAYGROUND.TITLE')">
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ t('ARROWS_AI.PLAYGROUND.DESCRIPTION') }}
      </p>
      <div
        class="min-h-64 rounded-xl border border-n-weak bg-n-solid-1 p-4 space-y-3"
      >
        <p v-if="!replies.length" class="text-sm text-n-slate-11">
          {{ t('ARROWS_AI.PLAYGROUND.EMPTY') }}
        </p>
        <article
          v-for="reply in replies"
          :key="reply.id"
          class="rounded-lg px-3 py-2 text-sm"
          :class="
            reply.role === 'user'
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'bg-n-solid-2 text-n-slate-11'
          "
        >
          <div
            v-if="reply.role === 'assistant'"
            v-dompurify-html="formatReply(reply.text)"
            class="prose prose-bubble max-w-none text-sm text-n-slate-12"
          />
          <p v-else class="mb-0 whitespace-pre-wrap">{{ reply.text }}</p>
        </article>
        <p v-if="isSending" class="text-sm text-n-slate-11">
          {{ t('ARROWS_AI.PLAYGROUND.SENDING') }}
        </p>
      </div>
      <form class="flex gap-2" @submit.prevent="sendPreview">
        <textarea
          v-model="message"
          rows="3"
          :disabled="isSending"
          class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          :placeholder="t('ARROWS_AI.PLAYGROUND.PLACEHOLDER')"
          @keydown.enter.exact="handleEnterKey"
        />
        <Button
          type="submit"
          :label="t('ARROWS_AI.PLAYGROUND.SEND')"
          :is-loading="isSending"
          :disabled="isSending"
        />
      </form>
    </div>
  </PageLayout>
</template>
